import { CURRENCY, NUMBER, PERCENTAGE } from 'ui/card/constants';
import { colors, fonts, fontSizes, fontWeights } from 'ui/theme';
import { formatTrimmedPercent } from 'utils/numberHelpers';
import {
  AggMetricResponse,
  AllFilters,
  AggMetricDataByMedium,
  Metric,
} from 'modules/messagingInsights/types';
import {
  getMetricMedium,
  sanitizeMediumParam,
  getUserFriendlyMetricLabel,
  compileSharedUrl,
} from 'modules/messagingInsights/utils';
import { metricSentenceCase } from 'modules/productAnalytics/utils';

import { ColChartMediumData, ColChartMetricData } from './types';
import {
  MediumOptions,
  HighLevelMediumAgnosticMetrics,
  colorsByMedium,
  MediumAgnosticLabel,
  NonMediumSpecificMetricOptionLabels,
} from '../../constants';

/**
 * Create highcharts-formatted time series chart data for each medium-specific metric
 * @param aggMetricData
 * @returns { ColChartMetricData[] } chart data for each medium-specific metric
 */
export const getMediumSpecificChartData = (
  aggMetricData: AggMetricResponse,
  analyticsMetrics: Metric[],
) => {
  const mediumSpecificData = aggMetricData.mediumMetrics || {};

  const mediumSpecificHistogramData = Object.keys(mediumSpecificData).flatMap((metric) => {
    return Object.keys(mediumSpecificData[metric]).includes('histogram')
      ? {
          metric,
          data: [
            {
              name: NonMediumSpecificMetricOptionLabels.has(metric)
                ? MediumAgnosticLabel
                : MediumOptions[getMetricMedium(metric, analyticsMetrics)],
              data: mediumSpecificData[metric].histogram?.buckets.map((data) => [
                data.time,
                data.value,
              ]),
            },
          ],
        }
      : [];
  });
  return mediumSpecificHistogramData;
};

/**
 * Create highcharts-formatted time series data for each medium belonging to a high-level metric
 * @param overallMetricData
 * @returns { ColChartMediumData[] } an array of objects with the name of each medium and the
 * corresponding chart data
 */
export const getChartDataPerMedium = (
  overallMetricData: {
    value: number;
    mediums: AggMetricDataByMedium;
    histogram: {
      buckets: { time: number; value: number }[];
    };
  },
  metric: string,
) => {
  const dataByMedium: AggMetricDataByMedium = overallMetricData.mediums || {};
  const chartData = HighLevelMediumAgnosticMetrics.has(metric)
    ? [
        /** for medium-agnostic metrics (e.g. Total Unsubscribes, Revenue etc.), we want to use the histogram data
         * nested directly under the metric itself instead of the repeated/duplicate data belonging to the individual mediums */
        {
          name: MediumAgnosticLabel,
          data: overallMetricData.histogram?.buckets.map((data) => [data.time, data.value]),
        },
      ]
    : Object.keys(dataByMedium).map((medium) => ({
        name: medium,
        data: dataByMedium[medium].histogram?.buckets.map((data) => [data.time, data.value]),
      }));
  return chartData;
};

/**
 * Create highcharts-formatted time series chart data for each x-medium metric (1 chart for each overall metric)
 * @param aggMetricData aggregate metric data directly received from our BE endpoint
 * @returns { ColChartMetricData[] } data for all x-medium charts
 */
export const getXMediumChartData = (aggMetricData: AggMetricResponse) => {
  const overallMetrics = aggMetricData.overallMetrics || {};
  return Object.entries(overallMetrics).map(([metric, value]) => ({
    metric,
    data: getChartDataPerMedium(value, metric),
  }));
};

/**
 * @param chartData
 * @returns an array of colors for highcharts to display depending on which mediums
 * are displayed (all cols corresponding to Emails are purple, all bars corresponding to SMS are yellow, etc.)
 */
export const getMediumColors = (chartData: ColChartMediumData[]) =>
  chartData.map((medium) => colorsByMedium[medium.name] || colors.red50); // medium-agnostic bars will be red

const baseOptions = {
  chart: {
    style: {
      fontFamily: fonts.sansSerif,
      fontSize: fontSizes.s14,
    },
  },
  legend: {
    itemDistance: 60,
    margin: 20,
    itemStyle: {
      color: colors.textPrimary,
      fontWeight: fontWeights.regular,
      fontSize: fontSizes.s14,
    },
  },
  exporting: {
    enabled: true,
    buttons: {
      contextButton: {
        align: 'right',
        x: -20,
        y: -10,
        symbolFill: colors.textPrimary,
      },
    },
  },
  xAxis: {
    type: 'datetime',
    dateTimeLabelFormats: {
      minute: '%l:%M%P',
      hour: '%l:%M%P', // 1:05pm
      week: '%b %e', // June 6
      day: '%b %e',
    },
    labels: {
      style: {
        fontSize: fontSizes.s14,
      },
    },
  },
};

/**
 * Generate different options for the column charts based on what type of metric is displayed.
 * If a metric is a rate/percentage, we display cols for each medium side-by-side.
 * If a metric is count/revenue-based, we display a stacked column chart.
 * @param metricChartData
 * @returns
 */
export const getChartOptions = (
  metricChartData: ColChartMetricData,
  numCampaignsPerMedium: Record<string, number>,
  allFilters: AllFilters,
  metricType: string,
) => {
  const metricColors = getMediumColors(metricChartData.data);
  const options = { ...baseOptions, colors: metricColors };
  const allMediums = Object.keys(MediumOptions);

  const getTooltipBtnText = (medium: string) => {
    if (medium === MediumAgnosticLabel) {
      return 'View these campaigns';
    } else {
      const numberOfCampaigns = numCampaignsPerMedium[sanitizeMediumParam(medium)] || 0;
      return metricSentenceCase(
        numberOfCampaigns >= 1
          ? `View ${numberOfCampaigns === 1 ? 'this' : 'these'} ${numberOfCampaigns} ${medium} ${
              numberOfCampaigns === 1 ? 'campaign' : 'campaigns'
            }`
          : `View ${medium} campaigns`,
      );
    }
  };

  if (metricType === PERCENTAGE) {
    return {
      ...options,
      yAxis: {
        min: 0,
        title: {
          text: 'Rate',
          margin: 25,
        },
        labels: {
          // @ts-ignore <-- need this since we're using the Highcharts-dictated formatting fn
          formatter() {
            // @ts-ignore
            return `${formatTrimmedPercent(this.value)}`;
          },
          style: {
            color: colors.grey90,
            fontSize: fontSizes.s14,
          },
        },
      },
      plotOptions: {
        column: {
          groupPadding: 0.2,
          pointPadding: 0.05,
        },
      },
      tooltip: {
        useHTML: true,
        style: {
          pointerEvents: 'auto',
        },
        // @ts-ignore
        pointFormatter() {
          // @ts-ignore
          const medium = this.series.name;
          const mediumSpecificFilters: AllFilters = {
            ...allFilters,
            selectedMediums:
              medium === MediumAgnosticLabel ? allMediums : [sanitizeMediumParam(medium)],
          };
          // @ts-ignore
          return `<br/><span style="color:${this.color}">\u25CF</span> ${medium}: ${
            // @ts-ignore
            (this.y * 100).toFixed(2)
          }%<div style="display:flex; justify-content: center; margin-top: 0.5rem;"><a href=${
            compileSharedUrl(mediumSpecificFilters)?.completeUrl
          }><button style="padding: 0.25rem 1rem;">${getTooltipBtnText(medium)}</button></a></div>`;
        },
      },
      exporting: {
        enabled: false,
      },
    };
  } else {
    return {
      ...options,
      yAxis: {
        min: 0,
        title: {
          text: metricType === NUMBER ? 'Event count' : 'Revenue',
          margin: 25,
        },
        labels: {
          /* eslint-disable no-template-curly-in-string */
          format: metricType === NUMBER ? '{value}' : '${value}',
          style: {
            color: colors.grey90,
            fontSize: fontSizes.s14,
          },
        },
        lineWidth: 1,
        lineColor: colors.grey15,
      },
      tooltip: {
        useHTML: true,
        style: {
          pointerEvents: 'auto',
        },
        // @ts-ignore
        pointFormatter() {
          // @ts-ignore
          const medium = this.series.name;
          const mediumSpecificFilters: AllFilters = {
            ...allFilters,
            selectedMediums:
              medium === MediumAgnosticLabel ? allMediums : [sanitizeMediumParam(medium)],
          };
          // @ts-ignore
          return `<br/><span style="color:${this.color}">\u25CF</span> ${medium}: ${
            // @ts-ignore
            metricType === CURRENCY ? `$${this.y.toFixed(2)}` : this.y
          }<div style="display:flex; justify-content: center; margin-top: 0.5rem;"><a href=${
            compileSharedUrl(mediumSpecificFilters)?.completeUrl
          }><button style="padding: 0.25rem 1rem;">${getTooltipBtnText(medium)}</button></a></div>`;
        },
      },
      plotOptions: {
        column: {
          stacking: 'normal',
        },
      },
      exporting: {
        enabled: false,
      },
    };
  }
};

/**
 * @param metricList list of metric names e.g. ['TotalEmailSends', 'TotalPushSends', ...]
 * @returns loading/placeholder empty data for populating the time series charts
 */
export const getLoadingData = (metricList: string[]) =>
  metricList.map((metricName) => ({ metric: getUserFriendlyMetricLabel(metricName), data: [] }));

/**
 * @param metricOrder list of metric names e.g. ['TotalEmailSends', 'TotalPushSends', ...] that match
 * the order in which metrics are selected from the metric MultiSelect
 * @param unsortedData unsorted chart data
 * @returns chart data sorted based on the ordering dictated in metricOrder
 */
export const sortBySelectionOrder = (metricOrder: string[], unsortedData: ColChartMetricData[]) => {
  const metricLabelOrder = metricOrder.map((metric) => getUserFriendlyMetricLabel(metric));
  const sortedData = [...unsortedData];
  sortedData.sort(
    (a, b) => metricLabelOrder.indexOf(a.metric) - metricLabelOrder.indexOf(b.metric),
  );
  return sortedData;
};
